/**
 * homePage.styles.ts — Styled-components LOCAUX de la landing fusionnée (Home + Get Started).
 *
 * Layouts spécifiques à la landing (hero split, hud, features, steps, dlcard, earn, gallery)
 * NON couverts par les primitives daV2/daComponents. Composent les tokens de theme.js UNIQUEMENT
 * (§20 anti-drift : zéro littéral couleur/font/radius/gradient local ; tout vient de ./theme).
 *
 * Rendu cible 1:1 (§43) = build/previews/dav2-fusion-B.html.
 * Les primitives réutilisables (CardV2, CornerRibbon, Eyebrow, SectionHead/Title/Bar/Count,
 * HudRow/Box/Label/Value, DividerGradient) sont importées depuis daV2.jsx dans homePage.tsx.
 */
import styled from 'styled-components';
import {COLORS, FONTS, RADII, SHADOW, GRADIENTS, HEADER_CLEAR, LAYOUT, media as mediaTokens, FONT_SIZE, FONT_WEIGHT} from '../../components/ui/theme';
import { Eyebrow as DaEyebrow, SectionHead as DaSectionHead, SectionTitle as DaSectionTitle } from '../../components/ui/daV2';

// `media` est construit via reduce depuis {} dans theme.js (non typé) → TS l'infère `{}`
// et refuse media.lg (TS2339). Cast unique pour conserver les 11 usages `media.lg` inchangés.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const media: any = mediaTokens;

/* ════════════════════════════════════════════════════════════════════════════
 * OVERRIDES DA V2 SCOPÉS LANDING — les primitives daV2 (Eyebrow/SectionHead/SectionTitle)
 * sont tunées pour d'autres pages ; ici on cale EXACTEMENT sur la preview dav2-fusion-B :
 *  - eyebrow hero/earn : .72rem (daV2 = .66rem)
 *  - section-head : margin 0 0 1.6rem (daV2 = 2.2rem 0 1.3rem → +35px de trop en haut, ×3 titres)
 *  - section title : clamp(1.4rem,3vw,1.9rem) (daV2 plafonne à 1.5rem → titres trop petits)
 * styled(Base) = +1 niveau de spécificité → bat le défaut sans toucher la primitive partagée.
 * ════════════════════════════════════════════════════════════════════════════ */
export const HeroEyebrow = styled(DaEyebrow)<{ $lg?: boolean }>`
  /* $lg = eyebrow HERO agrandi +35% (.72→.97rem) + bold (Jonathan 2026-06-11). Roboto Mono
     plafonne à 700 (max chargé/dispo) = bold. L'eyebrow Earn (sans $lg) garde .72rem. */
  font-size: ${(p) => (p.$lg ? '0.97rem' : '0.72rem')};
  font-weight: ${FONT_WEIGHT.bold};
`;

export const LSectionHead = styled(DaSectionHead)`
  margin: 0 0 1.6rem;
  ${media.sm`
    margin: 0 0 1.2rem;
  `}
`;

export const LSectionTitle = styled(DaSectionTitle)`
  font-size: clamp(1.4rem, 3vw, 1.9rem);
`;

/* ─── WRAP central (token LAYOUT.page, padding latéral) ───────────────────────────────── */
export const Wrap = styled.div`
  max-width: ${LAYOUT.page};
  margin: 0 auto;
  padding: 0 1.6rem;
  /* Baseline texte = preview (Roboto 400, ink, line-height 1.5). Neutralise le global
     style.scss qui force body en "DM Sans" 300 → cause racine de la divergence de police
     live vs preview (texte body rendu en DM Sans au lieu de Roboto). Titres/eyebrows/mono
     gardent leur override explicite (FONTS.head / FONTS.mono). */
  font-family: ${FONTS.body};
  font-weight: ${FONT_WEIGHT.normal};
  line-height: 1.5;
  color: ${COLORS.ink};
`;

/* ════════════════════════════════════════════════════════════════════════════
 * BOUTONS landing (look "bold editorial" du preview — italic uppercase, halo).
 * Réutilisent les tokens ; déclinaisons spécifiques à la landing (taille/halo).
 * ════════════════════════════════════════════════════════════════════════════ */

/** Bouton primaire magenta (hero / download / earn) — italic 800 uppercase + halo accent. */
export const BtnPrimary = styled.button`
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.extrabold};
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: ${FONT_SIZE.base};
  color: #fff;
  background: ${COLORS.accent};
  border: none;
  border-radius: ${RADII.button};
  padding: 0.9em 1.9em;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
  box-shadow: ${SHADOW.accentGlow};

  &:hover {
    background: ${COLORS.accentHover};
    transform: translateY(-0.125rem);
  }
`;

/** Bouton outline (hero "Visit Marketplace"). */
export const BtnOutline = styled.button`
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: ${FONT_SIZE.base};
  color: ${COLORS.ink};
  background: ${COLORS.white};
  border: 0.0625rem solid ${COLORS.line};
  border-radius: ${RADII.button};
  padding: 0.9em 1.7em;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: ${COLORS.accent};
    color: ${COLORS.accent};
  }
`;

/** Bouton "View Specs" (bleu profond #20005e). */
export const BtnSpecs = styled.button`
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 0.95rem;
  color: #fff;
  background: ${COLORS.accent2Deep};
  border: none;
  border-radius: ${RADII.button};
  padding: 0.85em 1.6em;
  cursor: pointer;
  transition: filter 0.15s;

  &:hover {
    filter: brightness(1.15);
  }
`;

/** Lien-bouton transparent (wrappe un <a> autour des boutons sans casser le layout). */
export const LinkReset = styled.a`
  display: inline-flex;
  text-decoration: none;
`;

/* ════════════════════════════════════════════════════════════════════════════
 * HERO — split 1.1fr / 0.9fr + média cadre arrondi (corner ribbon + badge AAA·VR)
 * ════════════════════════════════════════════════════════════════════════════ */
export const Hero = styled.section`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 2.4rem;
  align-items: center;
  /* Header réel #myHeader est position:absolute (≈69px) et chevauche → clairance HEADER_CLEAR
     pour que le média/texte passent sous le header (la preview HTML avait un header sticky en flux). */
  padding: ${HEADER_CLEAR} 0 clamp(2rem, 4vw, 3rem);

  ${media.lg`
    grid-template-columns: 1fr;
  `}
`;

export const HeroTitle = styled.h1`
  font-family: ${FONTS.head};
  font-style: italic;
  font-weight: ${FONT_WEIGHT.extrabold};
  text-transform: uppercase;
  letter-spacing: -0.02em;
  line-height: 0.95;
  font-size: clamp(2.6rem, 6vw, 4.8rem);
  margin: 0.6rem 0 1rem;
  color: ${COLORS.ink};

  .hl {
    background: ${GRADIENTS.accent};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`;

export const HeroLead = styled.p`
  font-size: 1.1rem;
  color: ${COLORS.inkSoft};
  max-width: 34rem;
  line-height: 1.6;
`;

export const CtaRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.6rem;
`;

export const HeroMedia = styled.div`
  position: relative;
  border-radius: ${RADII.card};
  overflow: hidden;
  aspect-ratio: 4 / 5;
  background: ${COLORS.inkDeep};
  box-shadow: ${SHADOW.heroMedia};
  border-top: 0.3rem solid ${COLORS.accent};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${media.lg`
    aspect-ratio: 16 / 10;
    order: -1;
  `}
`;

export const HeroCorner = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  width: 2.6rem;
  height: 2.6rem;
  z-index: 3;
  background: ${GRADIENTS.accent};
  clip-path: polygon(100% 0, 100% 100%, 0 0);
`;

export const HeroBadge = styled.span`
  position: absolute;
  left: 0;
  top: 1.1rem;
  z-index: 3;
  background: ${GRADIENTS.goldBadgeFlat};
  color: ${COLORS.goldBadgeInk};
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.extrabold};
  font-style: italic;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  padding: 0.4em 1.1em 0.4em 0.8em;
  clip-path: polygon(0 0, 100% 0, calc(100% - 0.4rem) 100%, 0 100%);
`;

/* ════════════════════════════════════════════════════════════════════════════
 * HUD — 3 boîtes (label mono + valeur DM Sans italic 800)
 * Layout 3 colonnes spécifique landing (la primitive HudRow/HudValue est mono/accent ;
 * ici la valeur est DM Sans italic 800 ink → composant local fidèle au preview).
 * ════════════════════════════════════════════════════════════════════════════ */
export const HudGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.9rem;
  padding: 0; /* neutralise le legacy global section{padding:90px 0} (fuite d'espacement) */
  margin-bottom: clamp(2.5rem, 5vw, 3.5rem);

  ${media.lg`
    grid-template-columns: 1fr;
  `}
`;

export const HudBox = styled.div`
  position: relative;
  overflow: hidden;
  background: ${COLORS.white};
  border: 0.0625rem solid ${COLORS.line};
  border-radius: ${RADII.card};
  padding: 1.2rem 1.3rem;
  box-shadow: ${SHADOW.card};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0.24rem;
    background: ${COLORS.accent};
  }
`;

export const HudKey = styled.div`
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.xs};
  letter-spacing: 0.14em;
  color: ${COLORS.muted};
  text-transform: uppercase;
`;

export const HudVal = styled.div`
  font-family: ${FONTS.head};
  font-style: italic;
  font-weight: ${FONT_WEIGHT.extrabold};
  font-size: 1.7rem;
  color: ${COLORS.ink};
  margin-top: 0.25rem;
  line-height: 1;
`;

/* ════════════════════════════════════════════════════════════════════════════
 * FEATURES — 3 cartes (Explore / Create / Trade)
 * ════════════════════════════════════════════════════════════════════════════ */
export const FGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.4rem;
  padding: 0 0 clamp(3rem, 6vw, 4.5rem); /* top:0 neutralise legacy section{padding:90px} */

  ${media.lg`
    grid-template-columns: 1fr;
  `}
`;

/** Carte feature — clone fidèle (préfère un local au CardV2 pour caler les détails preview :
 *  thumb 16/10, body flex, fbtn flèche, variante "trade" verte). */
export const FCard = styled.article<{ $trade?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${COLORS.white};
  border-radius: ${RADII.card};
  overflow: hidden;
  box-shadow: ${SHADOW.card};
  border-top: 0.26rem solid ${(p) => (p.$trade ? COLORS.cta : COLORS.accent)};
  transition: transform 0.18s, box-shadow 0.18s;

  &:hover {
    transform: translateY(-0.375rem);
    box-shadow: ${SHADOW.hover};
  }

  ${media.lg`
    &:hover { transform: none; }
  `}
`;

export const FCorner = styled.span<{ $trade?: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 2.2rem;
  height: 2.2rem;
  z-index: 3;
  background: ${(p) => (p.$trade ? GRADIENTS.cta : GRADIENTS.accent)};
  clip-path: polygon(100% 0, 100% 100%, 0 0);
  pointer-events: none;
`;

export const FThumb = styled.div<{ $contain?: boolean }>`
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: ${(p) => (p.$contain ? GRADIENTS.thumbRadial : COLORS.inkDeep)};

  img {
    width: 100%;
    height: 100%;
    object-fit: ${(p) => (p.$contain ? 'contain' : 'cover')};
    transition: transform 0.35s;
  }

  ${FCard}:hover & img {
    transform: scale(1.06);
  }
`;

export const FBody = styled.div`
  padding: 1.3rem 1.3rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const FEyebrow = styled.span<{ $cta?: boolean }>`
  font-family: ${FONTS.mono};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: ${FONT_SIZE.xs};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${(p) => (p.$cta ? COLORS.cta : COLORS.accent)};
  margin-bottom: 0.5rem;
`;

export const FTitle = styled.h3`
  font-family: ${FONTS.head};
  font-style: italic;
  font-weight: ${FONT_WEIGHT.extrabold};
  text-transform: uppercase;
  letter-spacing: -0.01em;
  font-size: ${FONT_SIZE.xl};
  margin-bottom: 0.6rem;
  color: ${COLORS.ink};
`;

export const FText = styled.p`
  color: ${COLORS.muted};
  font-size: 0.92rem;
  line-height: 1.55;
  flex: 1;
`;

export const FBtn = styled.a<{ $cta?: boolean }>`
  margin-top: 1.2rem;
  align-self: flex-start;
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 0.86rem;
  color: ${(p) => (p.$cta ? 'var(--ev-cta, ' + COLORS.cta + ')' : COLORS.accent)};
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;

  &::after {
    content: '→';
    transition: transform 0.15s;
  }

  ${FCard}:hover &::after {
    transform: translateX(0.25rem);
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
 * STEPS — 3 step cards (corner ribbon)
 * ════════════════════════════════════════════════════════════════════════════ */
export const Steps = styled.section`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.4rem;
  padding: 0 0 clamp(2.5rem, 5vw, 3.5rem); /* top:0 neutralise legacy section{padding:90px} */

  ${media.lg`
    grid-template-columns: 1fr;
  `}
`;

export const Step = styled.div`
  position: relative;
  overflow: hidden;
  background: ${COLORS.white};
  border: 0.0625rem solid ${COLORS.line};
  border-radius: ${RADII.card};
  padding: 1.6rem 1.4rem;
  box-shadow: ${SHADOW.card};
  border-top: 0.26rem solid ${COLORS.accent};
`;

export const StepCorner = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  width: 2rem;
  height: 2rem;
  z-index: 3;
  background: ${GRADIENTS.accent};
  clip-path: polygon(100% 0, 100% 100%, 0 0);
`;

export const StepN = styled.div`
  font-family: ${FONTS.mono};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  color: ${COLORS.accent};
  text-transform: uppercase;
`;

export const StepTitle = styled.h4`
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 1.1rem;
  margin: 0.5rem 0 0.4rem;
  color: ${COLORS.ink};
`;

export const StepText = styled.p`
  color: ${COLORS.muted};
  font-size: 0.9rem;
  line-height: 1.5;
`;

/* ════════════════════════════════════════════════════════════════════════════
 * DOWNLOAD card — logo Epic + texte + boutons
 * ════════════════════════════════════════════════════════════════════════════ */
export const DlCard = styled.section`
  background: ${COLORS.white};
  border-radius: ${RADII.card};
  border-top: 0.3rem solid ${COLORS.accent};
  box-shadow: ${SHADOW.dlcard};
  padding: clamp(2rem, 4vw, 3rem);
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: center;
  margin-bottom: clamp(3rem, 6vw, 4.5rem);

  ${media.lg`
    grid-template-columns: 1fr;
    text-align: center;
  `}
`;

export const DlEpic = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;

  img {
    width: 7rem;
    height: 7rem;
    border-radius: ${RADII.card};
    object-fit: cover;
  }
`;

export const DlEpicTag = styled.span`
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.xs};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${COLORS.muted};
`;

export const DlTitle = styled.h3`
  font-family: ${FONTS.head};
  font-style: italic;
  font-weight: ${FONT_WEIGHT.extrabold};
  text-transform: uppercase;
  letter-spacing: -0.02em;
  font-size: clamp(1.6rem, 3.5vw, 2.4rem);
  margin-bottom: 0.5rem;
  color: ${COLORS.ink};
`;

export const DlText = styled.p`
  color: ${COLORS.muted};
  font-size: 0.95rem;
  max-width: 34rem;
`;

export const DlNote = styled.div`
  font-style: italic;
  font-size: 0.82rem;
  color: ${COLORS.muted};
  margin: 0.7rem 0 1rem;
`;

export const DlBtns = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;

  ${media.lg`
    justify-content: center;
  `}
`;

/* ════════════════════════════════════════════════════════════════════════════
 * EARN — bande dégradé sombre + média voiture (halo radial + scale 1.63)
 * ════════════════════════════════════════════════════════════════════════════ */
export const Earn = styled.section`
  background: ${GRADIENTS.dark};
  border-radius: ${RADII.card};
  overflow: visible;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 2rem;
  padding: 0; /* neutralise legacy section{padding:90px 0} (padding interne sur EarnCopy/EarnMedia) */
  margin: 2.5rem 0 4rem;
  box-shadow: ${SHADOW.heroMedia};

  ${media.lg`
    grid-template-columns: 1fr;
    /* Mobile : clippe le débordement latéral de l'image car (scale 1.63 → boîte > viewport),
       cause racine mesurée du scroll horizontal de la home (§décision 8). */
    overflow-x: clip;
  `}
`;

export const EarnMedia = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 18rem;
  padding: clamp(1rem, 3vw, 2.2rem);

  &::before {
    content: '';
    position: absolute;
    width: 78%;
    height: 62%;
    border-radius: 50%;
    background: ${GRADIENTS.earnHalo};
    filter: blur(2rem);
    z-index: 0;
  }

  img {
    position: relative;
    z-index: 1;
    width: 100%;
    height: auto;
    max-height: clamp(13rem, 26vw, 20rem);
    object-fit: contain;
    display: block;
    transform: scale(1.63);
    transform-origin: center;
    filter: ${SHADOW.earnCar};
  }

  ${media.lg`
    order: -1;
    /* Mobile : on neutralise le scale(1.63) — l'image (width:100%) tient alors dans sa
       colonne et ne déborde plus du viewport. Visuel desktop (scale 1.63) intact. */
    img { transform: scale(1); }
  `}
`;

export const EarnCopy = styled.div`
  padding: clamp(2rem, 4vw, 3.2rem);
  color: #fff;
`;

export const EarnTitle = styled.h2`
  font-family: ${FONTS.head};
  font-style: italic;
  font-weight: ${FONT_WEIGHT.extrabold};
  text-transform: uppercase;
  letter-spacing: -0.02em;
  line-height: 0.95;
  font-size: clamp(2rem, 4vw, 3.2rem);
  color: #fff;
`;

export const EarnBar = styled.div`
  height: 0.18rem;
  width: 5rem;
  background: ${GRADIENTS.accent};
  border-radius: 0.125rem;
  margin: 1.2rem 0 1.4rem;
`;

/* ════════════════════════════════════════════════════════════════════════════
 * GALLERY — 2 images arrondies
 * ════════════════════════════════════════════════════════════════════════════ */
export const Gallery = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.4rem;
  padding: 0 0 clamp(3rem, 6vw, 5rem); /* top:0 neutralise legacy section{padding:90px} */

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${RADII.card};
    box-shadow: ${SHADOW.card};
  }

  ${media.lg`
    grid-template-columns: 1fr;
  `}
`;
